import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  FiUpload, FiTrash2, FiArrowUp, FiArrowDown, FiCornerDownLeft, FiImage, FiEdit3,
} from 'react-icons/fi';
import { API_URL } from '../config';
import PhotoAnnotator from './PhotoAnnotator';

/**
 * Upload, caption and arrange the photos attached to a letter.
 *
 * Placement decides where each photo prints. "Exhibit" photos collect into a
 * numbered PHOTOGRAPHIC EXHIBIT after the signature; "In letter" photos print
 * where a marker sits in the body, which `onInsertInline` drops at the cursor.
 *
 * Thumbnails are fetched as blobs rather than set as <img src>, because the
 * photo endpoint requires a bearer token that a plain <img> would not send.
 */
function LetterPhotoPanel({ letterId, photos, onPhotosChange, onInsertInline, onRequireSave }) {
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [drafts, setDrafts] = useState({});   // id -> caption being typed
  const [previews, setPreviews] = useState({});
  const fileRef = useRef();
  const objectUrls = useRef([]);
  // Markup editor state: the photo being marked up plus an object URL of its
  // ORIGINAL (unmarked) file, so re-editing starts from a clean photo.
  const [markupPhoto, setMarkupPhoto] = useState(null);
  const [markupUrl, setMarkupUrl] = useState(null);
  const [bmpSymbols, setBmpSymbols] = useState([]);

  const token = localStorage.getItem('authToken');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  // Resolve thumbnails for any photo we haven't fetched yet.
  useEffect(() => {
    let cancelled = false;
    (photos || []).forEach(async (p) => {
      if (previews[p.id]) return;
      try {
        const res = await axios.get(`${API_URL}/letters/${p.letter_id}/photos/${p.id}/image`, {
          ...config, responseType: 'blob',
        });
        if (cancelled) return;
        const url = URL.createObjectURL(res.data);
        objectUrls.current.push(url);
        setPreviews(prev => ({ ...prev, [p.id]: url }));
      } catch (err) {
        // A missing thumbnail is cosmetic — the photo still prints.
        console.warn('Could not load letter photo thumbnail', p.id, err);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/bmp-symbols`, config);
        if (!cancelled) setBmpSymbols(res.data || []);
      } catch (err) {
        console.warn('Could not load BMP symbols', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Release blob URLs when the panel goes away.
  useEffect(() => () => {
    objectUrls.current.forEach(URL.revokeObjectURL);
    objectUrls.current = [];
  }, []);

  const reload = useCallback(async (id) => {
    const res = await axios.get(`${API_URL}/letters/${id}/photos`, config);
    onPhotosChange(res.data || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onPhotosChange]);

  const handleUpload = async (files) => {
    if (!files || !files.length) return;
    // A brand-new letter has no id yet; save it so the photos have somewhere to go.
    let id = letterId;
    if (!id) {
      id = await onRequireSave();
      if (!id) return;
    }
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const form = new FormData();
        form.append('photo', file);
        form.append('placement', 'exhibit');
        await axios.post(`${API_URL}/letters/${id}/photos`, form, {
          headers: { ...config.headers, 'Content-Type': 'multipart/form-data' },
        });
      }
      await reload(id);
    } catch (err) {
      alert('Photo upload failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const patchPhoto = async (photo, body) => {
    setBusyId(photo.id);
    try {
      await axios.patch(`${API_URL}/letters/${photo.letter_id}/photos/${photo.id}`, body, config);
      await reload(photo.letter_id);
    } catch (err) {
      alert('Could not update photo: ' + (err.response?.data?.error || err.message));
    } finally {
      setBusyId(null);
    }
  };

  const saveCaption = (photo) => {
    const draft = drafts[photo.id];
    if (draft === undefined || draft === (photo.caption || '')) return;
    patchPhoto(photo, { caption: draft });
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm('Remove this photo from the letter?')) return;
    setBusyId(photo.id);
    try {
      await axios.delete(`${API_URL}/letters/${photo.letter_id}/photos/${photo.id}`, config);
      await reload(photo.letter_id);
    } catch (err) {
      alert('Could not delete photo: ' + (err.response?.data?.error || err.message));
    } finally {
      setBusyId(null);
    }
  };

  const move = async (index, delta) => {
    const list = [...photos];
    const target = index + delta;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    onPhotosChange(list); // optimistic, so the arrows feel instant
    try {
      const res = await axios.post(
        `${API_URL}/letters/${letterId}/photos/reorder`,
        { order: list.map(p => p.id) }, config,
      );
      onPhotosChange(res.data || list);
    } catch (err) {
      alert('Could not reorder: ' + (err.response?.data?.error || err.message));
      reload(letterId);
    }
  };

  // Markup always opens over the ORIGINAL photo, never over a previous render,
  // so repeated edits don't bake markup on top of markup.
  const openMarkup = async (photo) => {
    setBusyId(photo.id);
    try {
      const res = await axios.get(
        `${API_URL}/letters/${photo.letter_id}/photos/${photo.id}/original`,
        { ...config, responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      objectUrls.current.push(url);
      setMarkupUrl(url);
      setMarkupPhoto(photo);
    } catch (err) {
      alert('Could not open the photo: ' + (err.response?.data?.error || err.message));
    } finally {
      setBusyId(null);
    }
  };

  const saveMarkup = async ({ json, dataUrl, hasMarkup }) => {
    const photo = markupPhoto;
    await axios.post(
      `${API_URL}/letters/${photo.letter_id}/photos/${photo.id}/markup`,
      hasMarkup ? { image_data: dataUrl, annotation_json: json } : { clear: true },
      config,
    );
    // Drop the cached thumbnail so the gallery picks up the new render.
    setPreviews(prev => {
      const next = { ...prev };
      delete next[photo.id];
      return next;
    });
    await reload(photo.letter_id);
    setMarkupPhoto(null);
    setMarkupUrl(null);
  };

  const labelStyle = {
    display: 'block', fontSize: '11px', textTransform: 'uppercase',
    color: '#6b7280', fontWeight: 600, marginBottom: '4px',
  };

  const exhibitCount = (photos || []).filter(p => p.placement !== 'inline').length;
  let exhibitSeen = 0;

  return (
    <div style={{ marginBottom: '20px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <label style={labelStyle}>Photos</label>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {photos?.length
              ? `${photos.length} attached · ${exhibitCount} in the exhibit`
              : 'Add site photos and a caption for each one.'}
          </div>
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          style={{
            padding: '9px 15px', background: '#0f2942', color: 'white', border: 'none',
            borderRadius: '8px', cursor: uploading ? 'wait' : 'pointer', fontSize: '13px',
            fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px',
          }}
        >
          <FiUpload size={14} /> {uploading ? 'Uploading...' : 'Add Photos'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => handleUpload(e.target.files)}
          style={{ display: 'none' }}
        />
      </div>

      {!photos?.length ? (
        <div style={{
          padding: '22px', textAlign: 'center', color: '#9ca3af', fontSize: '13px',
          border: '1px dashed #d1d5db', borderRadius: '8px', background: '#fafafa',
        }}>
          <FiImage size={20} style={{ marginBottom: '6px' }} />
          <div>No photos yet. Photos print as a numbered exhibit after your signature.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {photos.map((photo, i) => {
            const inline = photo.placement === 'inline';
            if (!inline) exhibitSeen += 1;
            const busy = busyId === photo.id;
            return (
              <div
                key={photo.id}
                style={{
                  display: 'flex', gap: '12px', padding: '10px', border: '1px solid #e5e7eb',
                  borderRadius: '8px', background: busy ? '#f9fafb' : 'white', opacity: busy ? 0.6 : 1,
                }}
              >
                <div style={{
                  width: '104px', height: '78px', flex: '0 0 104px', borderRadius: '6px',
                  overflow: 'hidden', background: '#f3f4f6', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                }}>
                  {previews[photo.id] ? (
                    <img src={previews[photo.id]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    <FiImage size={20} color="#9ca3af" />
                  )}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', flexWrap: 'wrap' }}>
                    <span style={{
                      fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '999px',
                      background: inline ? '#eef2ff' : '#ecfdf5', color: inline ? '#4338ca' : '#047857',
                    }}>
                      {inline ? 'In letter' : `Photo ${exhibitSeen}`}
                    </span>
                    <select
                      value={photo.placement || 'exhibit'}
                      onChange={(e) => patchPhoto(photo, { placement: e.target.value })}
                      style={{ fontSize: '12px', padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: '5px' }}
                    >
                      <option value="exhibit">Exhibit at end</option>
                      <option value="inline">Inline in body</option>
                    </select>
                    <button
                      type="button"
                      onClick={() => openMarkup(photo)}
                      title="Draw, add text, and place BMP blocks on this photo"
                      style={{
                        fontSize: '12px', padding: '3px 8px',
                        border: '1px solid ' + (photo.has_markup ? '#0f2942' : '#d1d5db'),
                        borderRadius: '5px',
                        background: photo.has_markup ? '#0f2942' : 'white',
                        color: photo.has_markup ? 'white' : '#374151',
                        cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px',
                        fontWeight: photo.has_markup ? 600 : 400,
                      }}
                    >
                      <FiEdit3 size={11} /> {photo.has_markup ? 'Marked up' : 'Mark up'}
                    </button>
                    {inline && (
                      <button
                        type="button"
                        onClick={() => onInsertInline(photo.id)}
                        title="Drop this photo at the cursor in the letter body"
                        style={{
                          fontSize: '12px', padding: '3px 8px', border: '1px solid #d1d5db',
                          borderRadius: '5px', background: 'white', cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: '4px',
                        }}
                      >
                        <FiCornerDownLeft size={11} /> Insert at cursor
                      </button>
                    )}
                  </div>

                  <textarea
                    value={drafts[photo.id] !== undefined ? drafts[photo.id] : (photo.caption || '')}
                    onChange={(e) => setDrafts(prev => ({ ...prev, [photo.id]: e.target.value }))}
                    onBlur={() => saveCaption(photo)}
                    placeholder="Caption — what this photo shows, where it was taken from..."
                    rows={2}
                    style={{
                      width: '100%', padding: '7px 9px', border: '1px solid #d1d5db',
                      borderRadius: '6px', fontSize: '13px', resize: 'vertical',
                      boxSizing: 'border-box', fontFamily: 'inherit',
                    }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} title="Move up"
                    style={iconBtn(i === 0)}><FiArrowUp size={13} /></button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === photos.length - 1} title="Move down"
                    style={iconBtn(i === photos.length - 1)}><FiArrowDown size={13} /></button>
                  <button type="button" onClick={() => deletePhoto(photo)} title="Remove"
                    style={{ ...iconBtn(false), color: '#dc2626' }}><FiTrash2 size={13} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {markupPhoto && markupUrl && (
        <PhotoAnnotator
          imageUrl={markupUrl}
          annotationJson={markupPhoto.annotation_json}
          bmpSymbols={bmpSymbols}
          title={`Mark Up — ${markupPhoto.original_filename || 'Photo'}`}
          onSave={saveMarkup}
          onClose={() => { setMarkupPhoto(null); setMarkupUrl(null); }}
        />
      )}
    </div>
  );
}

const iconBtn = (disabled) => ({
  padding: '5px 7px', border: '1px solid #e5e7eb', borderRadius: '5px',
  background: 'white', cursor: disabled ? 'default' : 'pointer',
  opacity: disabled ? 0.35 : 1, color: '#374151', lineHeight: 0,
});

export default LetterPhotoPanel;
